import {
  createSelector,
  DefaultProjectorFn,
  MemoizedSelector,
} from '@ngrx/store';

import {
  AuthPartialState,
  authQuery as authTokenQuery,
} from '@cigna/auth/data-access';

import { AccessToken, JWT_NS } from '../auth/access-token.model';
import { ENTITLEMENT_INDIVIDUAL } from '@cigna/chcp/auth/util';
import { IdToken } from '../auth/id-token.model';

const selectFromAccessNS = <
  TPayload extends AccessToken[typeof JWT_NS],
  TProp extends keyof TPayload
>(
  prop: TProp,
): MemoizedSelector<
  AuthPartialState,
  TPayload[TProp] | undefined | null,
  DefaultProjectorFn<TPayload[TProp] | undefined | null>
> =>
  createSelector(authTokenQuery.getAuthData, (authData) =>
    authData != null
      ? ((authData.accessTokenBody as AccessToken)[JWT_NS] as TPayload)[prop]
      : authData,
  );

const selectFromIdentityNS = <
  TPayload extends IdToken[typeof JWT_NS],
  TProp extends keyof TPayload
>(
  prop: TProp,
): MemoizedSelector<
  AuthPartialState,
  TPayload[TProp] | undefined | null,
  DefaultProjectorFn<TPayload[TProp] | undefined | null>
> =>
  createSelector(authTokenQuery.getAuthData, (authData) =>
    authData != null
      ? ((authData.idTokenBody as IdToken)[JWT_NS] as TPayload)[prop]
      : authData,
  );

const getUserLob = selectFromAccessNS('lob');
const getUserLobId = createSelector(getUserLob, (lob) => {
  switch (lob) {
    case 'MEDBH': {
      return 1;
    }
    case 'DEN': {
      return 2;
    }
    case 'FDOHP': {
      return 3;
    }
    default: {
      return 1;
    }
  }
});

const getUserSub = createSelector(
  authTokenQuery.getAuthData,
  (authData) => (authData?.accessTokenBody as AccessToken).sub || null,
);
const getUserId = selectFromAccessNS('cn');
const isDentalUser = createSelector(getUserLob, (lob) => lob && lob === 'DEN');
// in most cases we treat fdohp as medical for purposes of UI areas where we show diff things for medical vs dental
const isMedicalUser = createSelector(
  getUserLob,
  (lob) => (lob && lob === 'MEDBH') || (lob && lob === 'FDOHP'),
);
// separate check for scenarios where we want to know specifically they are fdohp lob
const isOhpUser = createSelector(
  getUserLob,
  (lob) => (lob && lob === 'FDOHP') || false,
);

const authLevel = selectFromAccessNS('authLevel');

const operatorRole = selectFromAccessNS('operatorRole');
const operatorId = selectFromAccessNS('operatorId');
const isImpersonator = createSelector(operatorRole, (role) => !!role);

const isRestrictedImpersonator = createSelector(
  operatorRole,
  (role) => (role && role === 'CHCP_RES') || false,
);
const isUnRestrictedImpersonator = createSelector(
  operatorRole,
  (role) => (role && role === 'CHCP_UNRES') || false,
);

const isSalesDemoUser = createSelector(
  getUserId,
  (userid) => (userid && userid.startsWith('sales')) || false,
);

const getUserAuthEntitlements = selectFromAccessNS('entitlements');
const getPermissionToggles = selectFromAccessNS('permissionToggles');

const hasAllEntitlements = createSelector(
  getUserAuthEntitlements,
  (entitlements: string[], props: { entitlements: ENTITLEMENT_INDIVIDUAL[] }) =>
    entitlements
      ? props.entitlements.every((v) => entitlements.includes(v))
      : false,
);

const hasOneOfEntitlements = createSelector(
  getUserAuthEntitlements,
  (entitlements: string[], props: { entitlements: ENTITLEMENT_INDIVIDUAL[] }) =>
    entitlements
      ? props.entitlements.some((v) => entitlements.includes(v))
      : false,
);

const isUserAuthenticated = createSelector(
  getUserLob,
  (lob) => lob !== undefined,
);

const getUserFirstName = selectFromIdentityNS('firstName');
const getUserLastName = selectFromIdentityNS('lastName');

const getUserFullName = createSelector(
  getUserFirstName,
  getUserLastName,
  (first, last) => `${first} ${last}`,
);

export const authQuery = {
  getUserLob,
  getUserLobId,
  getUserSub,
  getUserId,
  isDentalUser,
  isMedicalUser,
  isOhpUser,
  getUserAuthEntitlements,
  hasAllEntitlements,
  hasOneOfEntitlements,
  isUserAuthenticated,
  authLevel,
  operatorId,
  operatorRole,
  isImpersonator,
  isRestrictedImpersonator,
  isUnRestrictedImpersonator,
  isSalesDemoUser,
  getPermissionToggles,
  getUserFullName,
};
